import React, { useState } from 'react'
import {
  Button,
  MenuItem,
  Checkbox,
  ListItemText,
  Avatar,
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@material-ui/core'
import { Add } from '@material-ui/icons'
import { makeStyles } from '@material-ui/core/styles'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import assignTeamDropdownStyles from './assignTeamDropdown.styles'
import SearchField from '../../../../components/ui/SearchField/SearchField'
import TextButton from '../../../../components/ui/TextButton/TextButton'
import useGetLocalStorage from '../../../../hooks/useGetLocalStorage'
import localStorageIds from '../../../../utils/constants/localStorageIds'
import ORGANISATION_TYPES from '../../../../utils/constants/OrganisationTypes'
import assignTeamToGroup from '../../../../Apis/groups/assignTeamToGroup'
import removeTeamFromGroup from '../../../../Apis/groups/removeTeamFromGroup'
import assignTeamToSeason from '../../../../Apis/season/assignTeamToSeason'
import removeTeamFromSeason from '../../../../Apis/season/removeTeamFromSeason'
import getTeams from '../../../../Apis/team/getTeams'
import QueryKeys from '../../../../utils/constants/queryKeys'
import getSeasonTeams from '../../../../Apis/season/getSeasonTeams'
import COLORS from '../../../../utils/constants/colors'

const useStyles = makeStyles(assignTeamDropdownStyles)

const AssignTeamDropdown = ({ orgId, seasonId, group, mode = 'group', gender, ageRestriction }) => {
  const classes = useStyles()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [pendingTeam, setPendingTeam] = useState(null)
  const [seasonTeamName, setSeasonTeamName] = useState('')
  const isSeasonMode = mode === 'season'

  const localStorageValue = useGetLocalStorage()
  const { [localStorageIds.SELECTED_ORG]: selectedOrg } = localStorageValue || {}
  const isFederation = selectedOrg?.type === ORGANISATION_TYPES.ORG_FEDERATION

  // Group mode: show season teams to assign to group
  const { data: seasonTeams, isLoading: seasonTeamsLoading } = useQuery(
    [QueryKeys.GET_SEASON_TEAMS, orgId, seasonId],
    () => getSeasonTeams(orgId, seasonId),
    {
      enabled: !isSeasonMode && Boolean(orgId && seasonId && open),
    }
  )

  // Season mode: show org teams to assign to season
  const { data: orgTeams, isLoading: orgTeamsLoading } = useQuery(
    [QueryKeys.GET_TEAMS, orgId, gender, ageRestriction],
    () => getTeams(orgId, { gender, ageRestriction }),
    {
      enabled: isSeasonMode && Boolean(orgId && open),
    }
  )

  // Season mode: also fetch current season teams to show checkmarks
  const { data: currentSeasonTeams } = useQuery(
    [QueryKeys.GET_SEASON_TEAMS, orgId, seasonId],
    () => getSeasonTeams(orgId, seasonId),
    {
      enabled: isSeasonMode && Boolean(orgId && seasonId && open),
    }
  )

  const teams = isSeasonMode ? orgTeams : seasonTeams
  const teamsLoading = isSeasonMode ? orgTeamsLoading : seasonTeamsLoading

  // Group mode mutations
  const assignToGroupMutation = useMutation(
    (seasonTeamId) => assignTeamToGroup({ orgId, groupId: group?.id, seasonTeamId }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QueryKeys.GET_STAGES, orgId, seasonId])
        queryClient.invalidateQueries(QueryKeys.GET_GROUP_TEAMS)
      },
    }
  )

  const removeFromGroupMutation = useMutation(
    (seasonTeamId) => removeTeamFromGroup({ orgId, groupId: group?.id, seasonTeamId }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QueryKeys.GET_STAGES, orgId, seasonId])
        queryClient.invalidateQueries(QueryKeys.GET_GROUP_TEAMS)
      },
    }
  )

  // Season mode mutations
  const assignToSeasonMutation = useMutation(
    ({ teamId, name }) => assignTeamToSeason({ orgId, seasonId, teamId, name }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QueryKeys.GET_SEASON_TEAMS, orgId, seasonId])
      },
    }
  )

  const removeFromSeasonMutation = useMutation(
    (seasonTeamId) => removeTeamFromSeason({ orgId, seasonTeamId }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QueryKeys.GET_SEASON_TEAMS, orgId, seasonId])
      },
    }
  )

  const handleClick = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    setSearch('')
  }

  const handleConfirmAssign = () => {
    assignToSeasonMutation.mutate({ teamId: pendingTeam.id, name: seasonTeamName })
    setPendingTeam(null)
    setSeasonTeamName('')
  }

  const handleCancelAssign = () => {
    setPendingTeam(null)
    setSeasonTeamName('')
  }

  const handleToggleTeam = (team) => {
    if (isSeasonMode) {
      const seasonTeam = currentSeasonTeams?.find((st) => st.teamId === team.id)
      if (seasonTeam) {
        removeFromSeasonMutation.mutate(seasonTeam.id)
      } else if (isFederation) {
        setPendingTeam(team)
        setSeasonTeamName(team.name || '')
      } else {
        assignToSeasonMutation.mutate({ teamId: team.id, name: undefined })
      }
    } else {
      const isAssigned = isTeamAssignedToGroup(team.id)
      if (isAssigned) {
        removeFromGroupMutation.mutate(team.id)
      } else {
        assignToGroupMutation.mutate(team.id)
      }
    }
  }

  const isTeamAssignedToGroup = (teamId) => {
    return group?.teams?.some((team) => team.seasonTeamId === teamId)
  }

  const isTeamAssigned = (teamId) => {
    if (isSeasonMode) {
      return currentSeasonTeams?.some((st) => st.teamId === teamId)
    }
    return isTeamAssignedToGroup(teamId)
  }

  const isTeamProcessing = (team) => {
    if (isSeasonMode) {
      const seasonTeam = currentSeasonTeams?.find((st) => st.teamId === team.id)
      return (
        (assignToSeasonMutation.isLoading && assignToSeasonMutation.variables?.teamId === team.id) ||
        (removeFromSeasonMutation.isLoading && removeFromSeasonMutation.variables === seasonTeam?.id)
      )
    }
    return (
      (assignToGroupMutation.isLoading && assignToGroupMutation.variables === team.id) ||
      (removeFromGroupMutation.isLoading && removeFromGroupMutation.variables === team.id)
    )
  }

  const filteredTeams = teams?.filter((team) =>
    team.name?.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <>
      <Button
        variant='contained'
        color='primary'
        startIcon={<Add />}
        onClick={handleClick}
      >
        {isSeasonMode ? 'Assign Team to Season' : 'Assign Team'}
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth='md' fullWidth>
        <DialogTitle>{isSeasonMode ? 'Assign Team to Season' : 'Assign Team'}</DialogTitle>
        <DialogContent style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '60vh' }}>
          <SearchField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            reset={() => setSearch('')}
            style={{ marginBottom: 8, backgroundColor: COLORS.backgroundGrey, width: '100%', borderRadius: 8, paddingLeft: 10, flexShrink: 0 }}
          />
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {teamsLoading ? (
              <div className={classes.loadingContainer}>
                <CircularProgress size={24} />
              </div>
            ) : filteredTeams && filteredTeams.length > 0 ? (
              filteredTeams.map((team) => {
                const isAssigned = isTeamAssigned(team.id)
                const isProcessing = isTeamProcessing(team)

                return (
                  <MenuItem
                    key={team.id}
                    onClick={() => handleToggleTeam(team)}
                    className={classes.menuItem}
                    disabled={isProcessing}
                  >
                    <Checkbox
                      checked={isAssigned}
                      disabled={isProcessing}
                      color='primary'
                    />
                    <div className={classes.teamInfo}>
                      <Avatar
                        src={team.teamLogoUrl || team.logoUrl}
                        alt={team.name}
                        className={classes.teamLogo}
                      >
                        {!team.teamLogoUrl && !team.logoUrl && team.name?.charAt(0)}
                      </Avatar>
                      <ListItemText primary={team.name} />
                    </div>
                    {isProcessing && <CircularProgress size={16} />}
                  </MenuItem>
                )
              })
            ) : (
              <div className={classes.emptyState}>
                <Typography variant='body2' color='textSecondary'>
                  No teams available
                </Typography>
              </div>
            )}
          </div>
        </DialogContent>
        <DialogActions style={{ justifyContent: 'space-between' }}>
          <Typography variant='caption' color='textSecondary'>
            {isSeasonMode
              ? "Showing teams that match the competition's age and gender restrictions."
              : 'Showing teams assigned to the season.'}
          </Typography>
          <TextButton label='Close' onClick={handleClose} />
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(pendingTeam)} onClose={handleCancelAssign} maxWidth='xs' fullWidth>
        <DialogTitle>Season Team Name</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label='Name'
            value={seasonTeamName}
            onChange={(e) => setSeasonTeamName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && seasonTeamName.trim() && handleConfirmAssign()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelAssign}>Cancel</Button>
          <Button
            onClick={handleConfirmAssign}
            color='primary'
            variant='contained'
            disabled={!seasonTeamName.trim()}
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default AssignTeamDropdown
